<template>
    <div class="buttons are-small">
        <template v-if="store.bulkModeEnabled">
            <button class="button toggle-event-registrations" @click="togglePage">
                <span class="icon">
                    <i class="fa fa-check-square" v-if="store.pageToggled" />
                    <i class="fa fa-square" v-else />
                </span>
            </button>

            <button class="button" @click="cancelBulkMode">
                <span class="icon">
                    <i class="fa fa-times" />
                </span>
                <span>{{ translate("common.cancel") }}</span>
            </button>

            <LoadingButton
                class="button is-success"
                @click="bulkUpdateCompetitorPayments(true)"
                :disabled="!isAnySelected"
                :is-loading="bulkUpdateInProgress === 'completing'"
            >
                <span class="icon">
                    <i class="fa fa-check" />
                </span>
                <span>
                    {{ translate("admin_payment.mark_all_complete") }}
                </span>
            </LoadingButton>
            <LoadingButton
                class="button is-danger"
                @click="bulkUpdateCompetitorPayments(false)"
                :disabled="!isAnySelected"
                :is-loading="bulkUpdateInProgress === 'disqualifying'"
            >
                <span class="icon">
                    <i class="fa fa-ban" />
                </span>
                <span>
                    {{ translate("admin_payment.mark_all_incomplete") }}
                </span>
            </LoadingButton>
        </template>
        <LoadingButton
            v-else
            class="button"
            @click="enableBulkMode"
            :is-loading="enablingBulkModeInProgress"
            :disabled="bulkModeCannotBeEnabled"
        >
            <span class="icon">
                <i class="fa fa-tasks" />
            </span>
            <span>{{ translate("admin_payment.select_many") }}</span>
        </LoadingButton>

        <p v-if="store.bulkModeEnabled" class="mb-2">
            <span class="is-description">{{ translate("common.selected_rows") }}:</span>
            <span class="has-text-weight-bold">{{ selectedCompetitorsCount }}</span>
            <a
                class="ml-2"
                v-if="store.matchingRows > PAGE_SIZE && store.selectionType !== 'searchCriteria'"
                @click="selectBySearchCriteria"
            >
                {{ translate("admin_payments.click_here_to_select_all_matching_results", false, [store.matchingRows]) }}
            </a>
            <span class="ml-2" v-else-if="store.selectionType === 'searchCriteria'">
                {{ translate("admin_payments.all_matching_results_selected") }}
            </span>
        </p>
    </div>
</template>

<script setup lang="ts">
    import { translate } from "../Common/translations";
    import { computed, ref } from "vue";
    import { useRegisteredCompetitorsStore } from "./registeredCompetitorsStore";
    import { PAGE_SIZE } from "./registeredCompetitorsStore";
    import { handleError } from "../Common/errorHandler";
    import LoadingButton from "../Common/LoadingButton.vue";
    import { successToast } from "../Common/toasts";

    const store = useRegisteredCompetitorsStore();

    const bulkModeCannotBeEnabled = computed(() => {
        return store.deleted;
    });

    const selectedCompetitorsCount = computed(() => {
        if (store.selectionType === "searchCriteria") {
            return store.matchingRows;
        }
        //If checkbox selection style, then just count number of manually selected competitors.
        return Object.keys(store.selectedCompetitors).filter(eventRegistrationId => store.selectedCompetitors[Number(eventRegistrationId)])
            .length;
    });

    const enablingBulkModeInProgress = ref(false);

    const enableBulkMode = () => {
        enablingBulkModeInProgress.value = true;
        store.enableBulkMode().finally(() => {
            enablingBulkModeInProgress.value = false;
        });
    };

    const cancelBulkMode = () => {
        store.cancelBulkMode();
    };

    const isAnySelected = computed(
        () =>
            Object.keys(store.selectedCompetitors).filter(eventRegistrationId => store.selectedCompetitors[Number(eventRegistrationId)])
                .length > 0,
    );

    const bulkUpdateInProgress = ref<"completing" | "disqualifying" | null>(null);

    const bulkUpdateCompetitorPayments = (isComplete: boolean) => {
        bulkUpdateInProgress.value = isComplete ? "completing" : "disqualifying";

        return store
            .bulkUpdateCompetitorPayments(isComplete)
            .then(() => {
                successToast(translate("page.save_competitors.bulk_complete"));
                cancelBulkMode();

                return store.getRegisteredCompetitorsPage();
            })
            .catch(handleError)
            .finally(() => {
                bulkUpdateInProgress.value = null;
            });
    };

    const togglePage = () => {
        store.togglePage(!store.pageToggled);
    };

    const selectBySearchCriteria = () => {
        store.selectBySearchCriteria();
    };

    defineExpose({});
</script>

<style scoped></style>
